import Fuse from 'fuse.js';

import type { PlaygroundPlace } from '~/types/Playground.types';

export const THRESHOLD = 0.2;
export const SCORE_CEILING = 0.15;
export const IGNORE_DIACRITICS = true;
export const KEYS = ['n'] as const;
export const PREFIX_RATIO_FLOOR = 0.6;
export const MIN_QUERY_LENGTH = 2;
export const MAX_QUERY_LENGTH = 200;

const CONNECTOR_TOKENS = new Set(['and', '&']);

export type MatchResult =
  | { kind: 'single'; placeId: number; placeName: string }
  | { kind: 'multi' }
  | { kind: 'unreachable' }
  | { kind: 'none' };

function normalise(s: string): string {
  return s
    .toLowerCase()
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '');
}

function tokenise(normalisedQuery: string): string[] {
  return normalisedQuery
    .replaceAll(',', ' ')
    .split(/\s+/)
    .filter((token) => token.length > 0 && !CONNECTOR_TOKENS.has(token));
}

function findExactSelectable(
  normalisedQuery: string,
  selectablePlaces: ReadonlyArray<PlaygroundPlace>
): { placeId: number; placeName: string } | 'ambiguous' | null {
  const exactMatches = selectablePlaces.filter((p) => normalise(p.n) === normalisedQuery);
  if (exactMatches.length === 1) {
    return { placeId: exactMatches[0].id, placeName: exactMatches[0].n };
  }
  if (exactMatches.length > 1) {
    return 'ambiguous';
  }
  return null;
}

function findFuzzySelectable(
  normalisedQuery: string,
  fuse: Fuse<PlaygroundPlace>
): { placeId: number; placeName: string } | null {
  const [top] = fuse.search(normalisedQuery);
  if (top?.score === undefined || top.score > SCORE_CEILING) {
    return null;
  }

  const candidateNormalised = normalise(top.item.n);

  // Prefix hardening: Fuse with ignoreLocation:false treats the query as a
  // substring match, so "rome" clears the score ceiling against "Frome". Require
  // the candidate to actually start with the query before committing to a pin.
  if (!candidateNormalised.startsWith(normalisedQuery)) {
    return null;
  }

  const candidateLength = candidateNormalised.length;
  if (candidateLength === 0 || normalisedQuery.length / candidateLength < PREFIX_RATIO_FLOOR) {
    return null;
  }

  return { placeId: top.item.id, placeName: top.item.n };
}

function findSinglePlace(
  normalisedQuery: string,
  selectablePlaces: ReadonlyArray<PlaygroundPlace>,
  fuse: Fuse<PlaygroundPlace>
): { placeId: number; placeName: string } | null {
  if (normalisedQuery.length < MIN_QUERY_LENGTH) {
    return null;
  }

  const exact = findExactSelectable(normalisedQuery, selectablePlaces);
  if (exact === 'ambiguous') {
    return null;
  }
  if (exact) {
    return exact;
  }

  return findFuzzySelectable(normalisedQuery, fuse);
}

export function matchSelectablePlace(
  query: string,
  selectablePlaces: ReadonlyArray<PlaygroundPlace>,
  nonSelectablePlaces: ReadonlyArray<PlaygroundPlace> = []
): MatchResult {
  const trimmed = query.trim().replace(/\s+/g, ' ');
  if (trimmed.length > MAX_QUERY_LENGTH) {
    return { kind: 'none' };
  }
  if (trimmed === '') {
    return { kind: 'none' };
  }

  const normalised = normalise(trimmed);
  if (normalised.length < MIN_QUERY_LENGTH) {
    return { kind: 'none' };
  }

  // Full-query exact pass against the selectable set takes precedence over
  // everything else.
  const exactSelectable = findExactSelectable(normalised, selectablePlaces);
  if (exactSelectable === 'ambiguous') {
    return { kind: 'none' };
  }
  if (exactSelectable) {
    return { kind: 'single', ...exactSelectable };
  }

  // Exact-known-place guard (evaluated on the full query, before the fuzzy pass):
  // if the query exactly names a place we know but cannot pin (unreachable or
  // unsupported), assume that place and fall through to AI rather than letting the
  // fuzzy pass attach it to a different selectable place.
  if (nonSelectablePlaces.some((p) => normalise(p.n) === normalised)) {
    return { kind: 'unreachable' };
  }

  const fuse = new Fuse(selectablePlaces.slice(), {
    keys: [...KEYS],
    threshold: THRESHOLD,
    ignoreDiacritics: IGNORE_DIACRITICS,
    ignoreLocation: false,
    includeScore: true
  });

  const fuzzyMatch = findFuzzySelectable(normalised, fuse);
  if (fuzzyMatch) {
    return { kind: 'single', ...fuzzyMatch };
  }

  const tokens = tokenise(normalised);
  if (tokens.length <= 1) {
    return { kind: 'none' };
  }

  const matchedIds = new Set<number>();
  for (const token of tokens) {
    const tokenMatch = findSinglePlace(token, selectablePlaces, fuse);
    if (tokenMatch) {
      matchedIds.add(tokenMatch.placeId);
    }
  }

  if (matchedIds.size >= 2) {
    return { kind: 'multi' };
  }

  return { kind: 'none' };
}
